import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MerchantOnboard = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: '',
    description: '',
    address: '',
    visits_for_reward: 5,
    reward_description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/merchant/onboard`,
        formData,
        { withCredentials: true }
      );
      
      toast.success('Merchant onboarded successfully!');
      navigate('/merchant');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onboarding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-[#18261E] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <div className="brand-logo text-4xl text-gray-100 mb-4" data-testid="brand-logo">habitué</div>
          <h1 className="text-3xl tracking-tight text-white mb-2" data-testid="onboard-title">Merchant Onboarding</h1>
          <p className="text-[#889990]">Set up your loyalty program in minutes</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-8" data-testid="onboard-form">
          <div className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Business Name *
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                required
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365]"
                placeholder="The Local Cafe"
                data-testid="business-name-input"
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Business Type *
              </label>
              <select
                value={formData.business_type}
                onChange={(e) => handleChange('business_type', e.target.value)}
                required
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all"
                data-testid="business-type-select"
              >
                <option value="" className="text-[#C3A365]">Select type</option>
                <option value="cafe">Cafe</option>
                <option value="restaurant">Restaurant</option>
                <option value="retail">Retail</option>
                <option value="salon">Salon</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all resize-none placeholder:text-[#C3A365]"
                placeholder="Brief description of your business"
                data-testid="description-input"
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365]"
                placeholder="123 Main St, City, State"
                data-testid="address-input"
              />
            </div>

            <div className="border-t border-[#3E5248] pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Loyalty Program Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                    Visits for Reward *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.visits_for_reward}
                    onChange={(e) => handleChange('visits_for_reward', parseInt(e.target.value))}
                    required
                    className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all"
                    data-testid="visits-for-reward-input"
                  />
                  <p className="text-xs text-[#889990] mt-1">Number of visits before customer earns a reward</p>
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                    Reward Description *
                  </label>
                  <input
                    type="text"
                    value={formData.reward_description}
                    onChange={(e) => handleChange('reward_description', e.target.value)}
                    required
                    className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365]"
                    placeholder="Free coffee"
                    data-testid="reward-description-input"
                  />
                  <p className="text-xs text-[#889990] mt-1">What customers get when they complete the visits</p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C3A365] text-[#18261E] hover:bg-white px-8 py-4 rounded-full font-medium tracking-wide text-base transition-all hover:-translate-y-1 hover:shadow-md active:scale-[0.97] disabled:opacity-50"
              data-testid="onboard-submit-button"
            >
              {loading ? 'Creating...' : 'Complete Onboarding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MerchantOnboard;
